import { Product } from '../types';
import { normalizeCategoryFolder } from './categoryFolders';

export interface HostingHealthReport {
  success: boolean;
  activeStorage: 'mysql' | 'file_json' | 'offline';
  authoritativeProductsCount: number;
  totalStoredImages: number;
  mysql: {
    configured: boolean;
    connected: boolean;
    host?: string;
    database?: string;
    user?: string;
    hasSavedPassword?: boolean;
    tablesReady?: boolean;
    productsCount: number;
    articlesCount?: number;
    error?: string | null;
  };
  fileStorage: {
    dataDirWritable: boolean;
    imagesDirWritable: boolean;
    productsJsonExists: boolean;
    productsJsonCount: number;
    productsJsonSizeBytes: number;
    lastUpdated?: string | null;
  };
  serverEnvironment?: {
    phpVersion?: string;
    uploadMaxFilesize?: string;
    postMaxSize?: string;
    memoryLimit?: string;
  };
}

export interface SyncProgressCallback {
  (progress: {
    stage: 'scanning' | 'uploading_images' | 'saving_catalog' | 'verifying' | 'complete' | 'error';
    message: string;
    percent: number;
    current: number;
    total: number;
  }): void;
}

/**
 * Checks connectivity and diagnostics of the hosting backend API
 */
export async function checkHostingHealth(): Promise<HostingHealthReport> {
  try {
    const res = await fetch(`/api/health.php?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Accept': 'application/json' }
    });

    if (!res.ok) {
      throw new Error(`Server returned status HTTP ${res.status}`);
    }

    const data = await res.json();
    return {
      success: true,
      activeStorage: data.active_storage || 'file_json',
      authoritativeProductsCount: data.authoritative_products_count || 0,
      totalStoredImages: data.total_stored_images || 0,
      mysql: {
        configured: Boolean(data.mysql?.configured),
        connected: Boolean(data.mysql?.connected),
        host: data.mysql?.host,
        database: data.mysql?.database,
        user: data.mysql?.user,
        hasSavedPassword: Boolean(data.mysql?.has_saved_password),
        tablesReady: Boolean(data.mysql?.tables_ready),
        productsCount: data.mysql?.products_count || 0,
        articlesCount: data.mysql?.articles_count || 0,
        error: data.mysql?.error
      },
      fileStorage: {
        dataDirWritable: Boolean(data.file_storage?.data_dir_writable),
        imagesDirWritable: Boolean(data.file_storage?.images_dir_writable),
        productsJsonExists: Boolean(data.file_storage?.products_json_exists),
        productsJsonCount: data.file_storage?.products_json_count || 0,
        productsJsonSizeBytes: data.file_storage?.products_json_size_bytes || 0,
        lastUpdated: data.file_storage?.last_updated
      },
      serverEnvironment: {
        phpVersion: data.server_environment?.php_version,
        uploadMaxFilesize: data.server_environment?.upload_max_filesize,
        postMaxSize: data.server_environment?.post_max_size,
        memoryLimit: data.server_environment?.memory_limit
      }
    };
  } catch (err: any) {
    return {
      success: false,
      activeStorage: 'offline',
      authoritativeProductsCount: 0,
      totalStoredImages: 0,
      mysql: {
        configured: false,
        connected: false,
        productsCount: 0,
        error: err?.message || 'Hosting API endpoint unreachable'
      },
      fileStorage: {
        dataDirWritable: false,
        imagesDirWritable: false,
        productsJsonExists: false,
        productsJsonCount: 0,
        productsJsonSizeBytes: 0
      }
    };
  }
}

/**
 * Tests MySQL connection credentials
 */
export async function testHostingDbConnection(config: {
  host?: string;
  database: string;
  username: string;
  password?: string;
  port?: string;
}): Promise<{ success: boolean; message: string }> {
  try {
    const res = await fetch('/api/setup_db.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'test_connection',
        host: config.host || 'localhost',
        database: config.database,
        username: config.username,
        password: config.password && config.password !== '••••••••' ? config.password : '__SAVED_PASSWORD__',
        port: config.port || '3306'
      })
    });

    const data = await res.json();
    if (res.ok && data.success) {
      return { success: true, message: data.message || 'Connected successfully to Hosting MySQL Database!' };
    }
    return { success: false, message: data.error || 'Failed to connect to database.' };
  } catch (e: any) {
    return { success: false, message: e?.message || 'Network error connecting to database setup API.' };
  }
}

/**
 * Saves MySQL credentials & auto-creates tables on hosting server
 */
export async function saveHostingDbConfig(config: {
  host?: string;
  database: string;
  username: string;
  password?: string;
  port?: string;
}): Promise<{ success: boolean; message: string }> {
  try {
    const res = await fetch('/api/setup_db.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'save_config',
        host: config.host || 'localhost',
        database: config.database,
        username: config.username,
        password: config.password && config.password !== '••••••••' ? config.password : '__SAVED_PASSWORD__',
        port: config.port || '3306'
      })
    });

    const data = await res.json();
    if (res.ok && data.success) {
      return { success: true, message: data.message || 'Hosting MySQL database configured and tables ready!' };
    }
    return { success: false, message: data.error || 'Failed to save configuration.' };
  } catch (e: any) {
    return { success: false, message: e?.message || 'Network error communicating with hosting server.' };
  }
}

/**
 * Migrates existing /data/products.json items into the Hosting MySQL Database
 */
export async function migrateJsonToHostingMySql(): Promise<{ success: boolean; count: number; message: string }> {
  try {
    const res = await fetch('/api/setup_db.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'migrate_json_to_mysql' })
    });

    const data = await res.json();
    if (res.ok && data.success) {
      return { success: true, count: data.count || 0, message: data.message };
    }
    return { success: false, count: 0, message: data.error || 'Migration failed.' };
  } catch (e: any) {
    return { success: false, count: 0, message: e?.message || 'Network error during migration.' };
  }
}

/**
 * Migrates existing /data/blog.json articles into Hosting MySQL Database
 */
export async function migrateBlogToHostingMySql(): Promise<{ success: boolean; count: number; message: string }> {
  try {
    const res = await fetch('/api/setup_db.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'migrate_blog_to_mysql' })
    });

    const data = await res.json();
    if (res.ok && data.success) {
      return { success: true, count: data.count || 0, message: data.message };
    }
    return { success: false, count: 0, message: data.error || 'Blog migration failed.' };
  } catch (e: any) {
    return { success: false, count: 0, message: e?.message || 'Network error during blog migration.' };
  }
}

/**
 * Fetch blog articles from hosting backend
 */
export async function fetchHostingBlogArticles(): Promise<any[] | null> {
  try {
    const res = await fetch(`/api/blog.php?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Accept': 'application/json' }
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.success && Array.isArray(data.articles)) {
      return data.articles;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Fetch aggregated blog traffic & engagement analytics from hosting MySQL database
 */
export async function fetchHostingBlogAnalytics(range: '7d' | '30d' | '90d' | 'all' = '30d'): Promise<any | null> {
  try {
    const res = await fetch(`/api/blog.php?action=analytics&range=${range}&t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Accept': 'application/json' }
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.success ? data : null;
  } catch {
    return null;
  }
}

/**
 * Reset all blog analytics counters (views, unique readers, WhatsApp clicks, read time, daily history) to 0 in MySQL & JSON
 */
export async function resetHostingBlogAnalytics(): Promise<boolean> {
  try {
    const res = await fetch('/api/blog.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'reset_analytics' })
    });
    if (!res.ok) return false;
    const data = await res.json();
    return Boolean(data.success);
  } catch {
    return false;
  }
}


/**
 * Save single blog article to hosting backend (MySQL + JSON)
 */
export async function saveHostingBlogArticle(article: any): Promise<boolean> {
  try {
    const res = await fetch('/api/blog.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'save_article', article })
    });
    const data = await res.json();
    return Boolean(res.ok && data.success);
  } catch {
    return false;
  }
}

/**
 * Delete blog article from hosting backend (MySQL + JSON)
 */
export async function deleteHostingBlogArticle(id: string): Promise<boolean> {
  try {
    const res = await fetch('/api/blog.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete_article', id })
    });
    const data = await res.json();
    return Boolean(res.ok && data.success);
  } catch {
    return false;
  }
}

/**
 * Save all blog articles to hosting backend (MySQL + JSON)
 */
export async function saveAllHostingBlogArticles(articles: any[]): Promise<boolean> {
  try {
    const res = await fetch('/api/blog.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'save_all', articles })
    });
    const data = await res.json();
    return Boolean(res.ok && data.success);
  } catch {
    return false;
  }
}

/**
 * Resolves a blob URL or base64 URL to a data URL for server upload
 */
async function resolveImageToDataUrl(url: string): Promise<string | null> {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('data:image/')) return url;
  if (url.startsWith('blob:')) {
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      return new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result);
          } else {
            resolve(null);
          }
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Full Sync: Uploads any embedded base64/blob images to hosting server, replaces them
 * with clean URLs, and saves the complete catalog to hosting database (MySQL & JSON).
 */
export async function syncCatalogToHosting(
  products: Product[],
  onProgress?: SyncProgressCallback
): Promise<{
  success: boolean;
  message: string;
  sanitizedProducts: Product[];
  imagesUploaded: number;
  serverCount: number;
  storage: string;
}> {
  const total = products.length;
  if (total === 0) {
    return {
      success: false,
      message: 'Catalog is empty. No products to sync.',
      sanitizedProducts: [],
      imagesUploaded: 0,
      serverCount: 0,
      storage: 'none'
    };
  }

  onProgress?.({
    stage: 'scanning',
    message: `Scanning ${total} products for images...`,
    percent: 5,
    current: 0,
    total
  });

  const sanitized: Product[] = [];
  let imagesUploaded = 0;

  for (let i = 0; i < total; i++) {
    const p = products[i];
    const cat = normalizeCategoryFolder(p.category);
    const prodCopy: Product = { ...p, category: cat as any };

    // 1. Process Main Image (base64 or blob)
    const isMainBase64 = prodCopy.image && prodCopy.image.startsWith('data:image/');
    const isMainBlob = prodCopy.image && prodCopy.image.startsWith('blob:');

    if (isMainBase64 || isMainBlob) {
      onProgress?.({
        stage: 'uploading_images',
        message: `Uploading image for [${i + 1}/${total}]: ${prodCopy.title}...`,
        percent: 10 + Math.round(((i + 1) / total) * 70),
        current: i + 1,
        total
      });

      try {
        const dataUrl = isMainBase64 ? prodCopy.image : await resolveImageToDataUrl(prodCopy.image);
        if (dataUrl) {
          const cleanName = `${(prodCopy.slug || prodCopy.id).replace(/[^a-zA-Z0-9_-]/g, '_')}_main`;
          const uploadRes = await fetch('/api/upload.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              category: cat,
              filename: `${cleanName}.webp`,
              image: dataUrl
            })
          });

          if (uploadRes.ok) {
            const uploadData = await uploadRes.json();
            if (uploadData && uploadData.success && uploadData.url) {
              prodCopy.image = uploadData.url;
              imagesUploaded++;
            }
          }
        }
      } catch (err) {
        console.warn(`Failed to upload main image for ${prodCopy.title}`, err);
      }
    }

    // 2. Process Additional Images
    if (Array.isArray(prodCopy.additionalImages) && prodCopy.additionalImages.length > 0) {
      const newAdditional: string[] = [];
      for (let j = 0; j < prodCopy.additionalImages.length; j++) {
        const addlUrl = prodCopy.additionalImages[j];
        const isAddlBase64 = addlUrl && addlUrl.startsWith('data:image/');
        const isAddlBlob = addlUrl && addlUrl.startsWith('blob:');

        if (isAddlBase64 || isAddlBlob) {
          try {
            const dataUrl = isAddlBase64 ? addlUrl : await resolveImageToDataUrl(addlUrl);
            if (dataUrl) {
              const cleanName = `${(prodCopy.slug || prodCopy.id).replace(/[^a-zA-Z0-9_-]/g, '_')}_extra_${j + 1}`;
              const addlRes = await fetch('/api/upload.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  category: cat,
                  filename: `${cleanName}.webp`,
                  image: dataUrl
                })
              });
              if (addlRes.ok) {
                const addlData = await addlRes.json();
                if (addlData && addlData.success && addlData.url) {
                  newAdditional.push(addlData.url);
                  imagesUploaded++;
                  continue;
                }
              }
            }
          } catch {}
        }
        newAdditional.push(addlUrl);
      }
      prodCopy.additionalImages = newAdditional;
    }

    sanitized.push(prodCopy);
  }

  // 3. Push complete sanitized catalog to hosting server-side products.php
  onProgress?.({
    stage: 'saving_catalog',
    message: `Saving ${total} products to hosting database...`,
    percent: 85,
    current: total,
    total
  });

  try {
    const saveRes = await fetch('/api/products.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode: 'replace',
        products: sanitized
      })
    });

    if (!saveRes.ok) {
      const errorText = await saveRes.text();
      throw new Error(`Hosting server returned HTTP ${saveRes.status}: ${errorText.slice(0, 120)}`);
    }

    const saveData = await saveRes.json();
    if (!saveData.success) {
      throw new Error(saveData.error || 'Hosting server rejected product save request.');
    }

    onProgress?.({
      stage: 'verifying',
      message: 'Verifying data on hosting server...',
      percent: 95,
      current: total,
      total
    });

    // 4. Verify by reading back from server
    const verifyRes = await fetch(`/api/products.php?t=${Date.now()}`, { cache: 'no-store' });
    const verifyData = await verifyRes.json();
    const serverCount = verifyData?.count ?? saveData.count;
    const storageMode = saveData.storage || verifyData?.storage || 'database';

    onProgress?.({
      stage: 'complete',
      message: `Complete! ${serverCount} products saved to hosting database (${storageMode}).`,
      percent: 100,
      current: total,
      total
    });

    return {
      success: true,
      message: `Successfully saved ${serverCount} products ${imagesUploaded > 0 ? `and ${imagesUploaded} images ` : ''}to hosting database!`,
      sanitizedProducts: sanitized,
      imagesUploaded,
      serverCount,
      storage: storageMode
    };
  } catch (err: any) {
    onProgress?.({
      stage: 'error',
      message: `Sync failed: ${err?.message || 'Server error'}`,
      percent: 100,
      current: total,
      total
    });

    return {
      success: false,
      message: `Failed to save to hosting server: ${err?.message || 'Unknown network error'}. Please check database connection and write permissions.`,
      sanitizedProducts: sanitized,
      imagesUploaded,
      serverCount: 0,
      storage: 'failed'
    };
  }
}
